import type { GroupColor, GroupIcon } from '@ralph/contracts'
import { DEFAULT_GROUP_ICON, GROUP_COLORS, GROUP_ICONS } from '@ralph/contracts'
import {
  Atom,
  Banknote,
  BriefcaseBusiness,
  Building,
  Calculator,
  ChartLine,
  Cloud,
  CodeXml,
  Coffee,
  Compass,
  Cpu,
  Dna,
  Factory,
  GraduationCap,
  HandHeart,
  Handshake,
  HardHat,
  Headset,
  HeartHandshake,
  HeartPulse,
  Landmark,
  Leaf,
  LifeBuoy,
  type LucideIcon,
  Mail,
  MapPin,
  Megaphone,
  Microscope,
  Network,
  Newspaper,
  Package,
  Palette,
  PiggyBank,
  Plane,
  Presentation,
  Puzzle,
  Receipt,
  Rocket,
  Scale,
  School,
  Server,
  ShoppingCart,
  Sprout,
  Stethoscope,
  Store,
  Trophy,
  Truck,
  UsersRound,
  Warehouse
} from 'lucide-react'

/**
 * The 48 group icons (PRD-26 D-6): the glyphs, and the structure of the grid they sit in.
 *
 * The set and its order are the contract's (`GROUP_ICONS`), so the server validates exactly what
 * the chooser offers. Every glyph the product already means something by — a lock, a globe, a
 * bot, a book — is absent from it on purpose: a group wearing one would say something it does
 * not mean.
 *
 * Each icon and each row also has a NAME, because the chooser is a radiogroup and a radio needs
 * a label. Those 54 words used to live here, where nothing extracted them and no visual pass
 * could see them — they render as `aria-label` and `title` (PRD-27 F-15). They are now
 * `t.groups.icons` and `t.groups.iconRows`, with everything else a person reads. What stays
 * here is the part that must be identical in every language (LOC-017).
 */

export const GROUP_ICON_COMPONENTS: Record<GroupIcon, LucideIcon> = {
  'users-round': UsersRound,
  handshake: Handshake,
  'heart-handshake': HeartHandshake,
  'hand-heart': HandHeart,
  network: Network,
  puzzle: Puzzle,
  trophy: Trophy,
  compass: Compass,
  'briefcase-business': BriefcaseBusiness,
  landmark: Landmark,
  scale: Scale,
  calculator: Calculator,
  banknote: Banknote,
  'piggy-bank': PiggyBank,
  receipt: Receipt,
  'chart-line': ChartLine,
  megaphone: Megaphone,
  newspaper: Newspaper,
  mail: Mail,
  headset: Headset,
  'life-buoy': LifeBuoy,
  store: Store,
  'shopping-cart': ShoppingCart,
  presentation: Presentation,
  'code-xml': CodeXml,
  server: Server,
  cloud: Cloud,
  cpu: Cpu,
  microscope: Microscope,
  atom: Atom,
  dna: Dna,
  rocket: Rocket,
  factory: Factory,
  'hard-hat': HardHat,
  truck: Truck,
  package: Package,
  warehouse: Warehouse,
  building: Building,
  'map-pin': MapPin,
  plane: Plane,
  'graduation-cap': GraduationCap,
  school: School,
  stethoscope: Stethoscope,
  'heart-pulse': HeartPulse,
  leaf: Leaf,
  sprout: Sprout,
  palette: Palette,
  coffee: Coffee
}

/**
 * The chooser's rows, in the contract's order: six categories of eight.
 *
 * `id` is what React keys on, what any code identifying a row compares, and what looks the row's
 * name up in the catalogue. It was once the name itself: the chooser keyed on the category NAME,
 * so translating it would have silently remounted every row of the grid — the same class of
 * defect as the command palette's group heading (PRD-27 F-9). Display text is never identity,
 * and the words live in `t.groups.iconRows`.
 */
export type GroupIconRow = 'groups' | 'business' | 'customers' | 'technology' | 'operations' | 'people'

export const GROUP_ICON_ROWS: ReadonlyArray<{ id: GroupIconRow; icons: readonly GroupIcon[] }> = [
  { id: 'groups', icons: GROUP_ICONS.slice(0, 8) },
  { id: 'business', icons: GROUP_ICONS.slice(8, 16) },
  { id: 'customers', icons: GROUP_ICONS.slice(16, 24) },
  { id: 'technology', icons: GROUP_ICONS.slice(24, 32) },
  { id: 'operations', icons: GROUP_ICONS.slice(32, 40) },
  { id: 'people', icons: GROUP_ICONS.slice(40, 48) }
]

/** A component for any stored value, falling back rather than failing — a retired icon still draws. */
export const groupIconComponent = (icon: string): LucideIcon =>
  GROUP_ICON_COMPONENTS[icon as GroupIcon] ?? GROUP_ICON_COMPONENTS[DEFAULT_GROUP_ICON]

/**
 * The colour fewest groups use, ties going to the palette's order — the server's own default for a
 * group created without one (D-6), chosen here so the new-group preview can show it. Counted over
 * the groups the page has read, which is every group on any deployment of a sensible size.
 */
export const leastUsedColor = (groups: readonly { color: string }[]): GroupColor => {
  const uses = new Map<GroupColor, number>(GROUP_COLORS.map((color) => [color, 0]))
  for (const group of groups) {
    const count = uses.get(group.color as GroupColor)
    if (count !== undefined) uses.set(group.color as GroupColor, count + 1)
  }
  let best: GroupColor = GROUP_COLORS[0]
  for (const color of GROUP_COLORS) if ((uses.get(color) ?? 0) < (uses.get(best) ?? 0)) best = color
  return best
}
